import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiCheckCircle,
    FiXCircle,
    FiAlertCircle,
    FiShield,
    FiUser,
    FiCreditCard,
    FiPercent,
    FiSave,
    FiX,
} from "react-icons/fi";

export default function BanksNominees() {
    const [showBankForm, setShowBankForm] = useState(false);
    const [showNomineeForm, setShowNomineeForm] = useState(false);
    const [editingBank, setEditingBank] = useState(null);
    const [editingNominee, setEditingNominee] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showOTPModal, setShowOTPModal] = useState(false);
    const [otp, setOtp] = useState("");

    // Bank form state
    const [bankForm, setBankForm] = useState({
        accountHolderName: "",
        accountNumber: "",
        ifscCode: "",
        bankName: "",
        branchName: "",
        accountType: "savings",
    });

    // Nominee form state
    const [nomineeForm, setNomineeForm] = useState({
        name: "",
        relationship: "",
        allocation: "",
        dateOfBirth: "",
        pan: "",
    });

    // Static data
    const [banks, setBanks] = useState([
        {
            id: 1,
            accountHolderName: "John Doe",
            accountNumber: "****1234",
            fullAccountNumber: "12345678901234",
            ifscCode: "HDFC0001234",
            bankName: "HDFC Bank",
            branchName: "Mumbai Main Branch",
            accountType: "Savings",
            isPrimary: true,
            isVerified: true,
        },
        {
            id: 2,
            accountHolderName: "John Doe",
            accountNumber: "****5678",
            fullAccountNumber: "56789012345678",
            ifscCode: "ICIC0005678",
            bankName: "ICICI Bank",
            branchName: "Mumbai Andheri",
            accountType: "Current",
            isPrimary: false,
            isVerified: false,
        },
    ]);

    const [nominees, setNominees] = useState([
        {
            id: 1,
            name: "Jane Doe",
            relationship: "Spouse",
            allocation: 50,
            dateOfBirth: "1992-08-20",
            pan: "FGHIJ5678K",
        },
        {
            id: 2,
            name: "John Doe Jr.",
            relationship: "Son",
            allocation: 50,
            dateOfBirth: "2015-03-10",
            pan: "KLMNO9012P",
        },
    ]);

    const handleBankInputChange = (e) => {
        const { name, value } = e.target;
        setBankForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleNomineeInputChange = (e) => {
        const { name, value } = e.target;
        setNomineeForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddBank = () => {
        setEditingBank(null);
        setBankForm({
            accountHolderName: "",
            accountNumber: "",
            ifscCode: "",
            bankName: "",
            branchName: "",
            accountType: "savings",
        });
        setShowBankForm(true);
    };

    const handleEditBank = (bank) => {
        setEditingBank(bank);
        setBankForm({
            accountHolderName: bank.accountHolderName,
            accountNumber: bank.fullAccountNumber,
            ifscCode: bank.ifscCode,
            bankName: bank.bankName,
            branchName: bank.branchName,
            accountType: bank.accountType.toLowerCase(),
        });
        setShowBankForm(true);
    };

    const handleSaveBank = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Validate IFSC format
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (!ifscRegex.test(bankForm.ifscCode.toUpperCase())) {
            alert("Invalid IFSC code format. Format: ABCD0123456");
            setIsSubmitting(false);
            return;
        }

        // If changing primary bank or editing existing bank, show OTP modal
        if (editingBank || banks.some((b) => b.isPrimary)) {
            setShowOTPModal(true);
            setIsSubmitting(false);
            return;
        }

        // Simulate API call
        setTimeout(() => {
            const newBank = {
                id: editingBank ? editingBank.id : banks.length + 1,
                accountHolderName: bankForm.accountHolderName,
                accountNumber: `****${bankForm.accountNumber.slice(-4)}`,
                fullAccountNumber: bankForm.accountNumber,
                ifscCode: bankForm.ifscCode.toUpperCase(),
                bankName: bankForm.bankName,
                branchName: bankForm.branchName,
                accountType: bankForm.accountType.charAt(0).toUpperCase() + bankForm.accountType.slice(1),
                isPrimary: banks.length === 0,
                isVerified: false,
            };

            if (editingBank) {
                setBanks((prev) => prev.map((b) => (b.id === editingBank.id ? newBank : b)));
            } else {
                setBanks((prev) => [...prev, newBank]);
            }

            setShowBankForm(false);
            setIsSubmitting(false);
            alert("Bank account saved successfully! Verification OTP will be sent.");
        }, 1000);
    };

    const handleVerifyOTP = () => {
        if (otp.length === 6) {
            setShowOTPModal(false);
            setOtp("");
            setIsSubmitting(true);
            setTimeout(() => {
                if (editingBank) {
                    setBanks((prev) =>
                        prev.map((b) => (b.id === editingBank.id ? { ...b, isVerified: true } : b))
                    );
                } else {
                    setBanks((prev) =>
                        prev.map((b, index) => (index === prev.length - 1 ? { ...b, isVerified: true } : b))
                    );
                }
                setIsSubmitting(false);
                setShowBankForm(false);
                alert("Bank account verified successfully!");
            }, 1000);
        } else {
            alert("Please enter a valid 6-digit OTP");
        }
    };

    const handleSetPrimary = (bankId) => {
        setShowOTPModal(true);
        setTimeout(() => {
            setBanks((prev) =>
                prev.map((b) => ({
                    ...b,
                    isPrimary: b.id === bankId,
                }))
            );
            setShowOTPModal(false);
            alert("Primary bank account updated successfully!");
        }, 1000);
    };

    const handleDeleteBank = (bankId) => {
        if (window.confirm("Are you sure you want to delete this bank account?")) {
            setBanks((prev) => prev.filter((b) => b.id !== bankId));
            alert("Bank account deleted successfully!");
        }
    };

    const handleAddNominee = () => {
        setEditingNominee(null);
        setNomineeForm({
            name: "",
            relationship: "",
            allocation: "",
            dateOfBirth: "",
            pan: "",
        });
        setShowNomineeForm(true);
    };

    const handleEditNominee = (nominee) => {
        setEditingNominee(nominee);
        setNomineeForm({
            name: nominee.name,
            relationship: nominee.relationship,
            allocation: nominee.allocation.toString(),
            dateOfBirth: nominee.dateOfBirth,
            pan: nominee.pan,
        });
        setShowNomineeForm(true);
    };

    const handleSaveNominee = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Validate allocation sum = 100%
        const currentTotal = nominees.reduce((sum, n) => (n.id === editingNominee?.id ? sum : sum + n.allocation), 0);
        const newAllocation = parseFloat(nomineeForm.allocation);
        const totalAllocation = currentTotal + newAllocation;

        if (totalAllocation > 100) {
            alert(`Total nominee allocation cannot exceed 100%. Current total: ${currentTotal}%, New: ${newAllocation}%`);
            setIsSubmitting(false);
            return;
        }

        // Validate PAN format
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(nomineeForm.pan.toUpperCase())) {
            alert("Invalid PAN format. PAN should be in format: ABCDE1234F");
            setIsSubmitting(false);
            return;
        }

        // Simulate API call
        setTimeout(() => {
            const newNominee = {
                id: editingNominee ? editingNominee.id : nominees.length + 1,
                name: nomineeForm.name,
                relationship: nomineeForm.relationship,
                allocation: parseFloat(nomineeForm.allocation),
                dateOfBirth: nomineeForm.dateOfBirth,
                pan: nomineeForm.pan.toUpperCase(),
            };

            if (editingNominee) {
                setNominees((prev) => prev.map((n) => (n.id === editingNominee.id ? newNominee : n)));
            } else {
                setNominees((prev) => [...prev, newNominee]);
            }

            setShowNomineeForm(false);
            setIsSubmitting(false);
            alert("Nominee saved successfully!");
        }, 1000);
    };

    const handleDeleteNominee = (nomineeId) => {
        if (window.confirm("Are you sure you want to delete this nominee?")) {
            setNominees((prev) => prev.filter((n) => n.id !== nomineeId));
            alert("Nominee deleted successfully!");
        }
    };

    const totalAllocation = nominees.reduce((sum, n) => sum + n.allocation, 0);

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div
                            className="card-body p-4"

                        >
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div>
                                    <h3 className="text-primary mb-2">Banks & Nominees</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">
                                                <NavLink to="/account/banks-nominees">Account</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item active">Banks & Nominees</li>
                                        </ol>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* Bank Accounts */}
                        <div className="col-lg-8">
                            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                <div
                                    className="card-header border-0 py-4 px-4 d-flex justify-content-between align-items-center"
                                    style={{
                                        background: "linear-gradient(135deg, rgb(248, 250, 252) 0%, rgb(241, 245, 249) 100%)",
                                        borderBottom: "none",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="me-3 d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "48px",
                                                height: "48px",
                                                borderRadius: "12px",
                                                background: "#c8eeecbf",
                                                // backdropFilter: "blur(10px)",
                                            }}
                                        >
                                            <FiCreditCard style={{ color: "#006e82", fontSize: "24px" }} />
                                        </div>
                                        {/* <h5 className="mb-0 fw-bold" style={{ color: "#ffffff", fontSize: "20px" }}>
                                            Bank Accounts
                                        </h5> */}
                                        <h5 className="mb-0 " style={{ color: "#006e82", fontSize: "20px" }}>
                                            Bank Accounts
                                        </h5>
                                    </div>
                                    <button
                                        className="btn btn-primary "
                                        onClick={handleAddBank}

                                
                                    >
                                        {/* <FiPlus className="me-1" />  */}
                                        Add Bank
                                    </button>
                                </div>
                                <div className="card-body p-4" style={{ backgroundColor: "#ffffff" }}>
                                    {banks.length === 0 ? (
                                        <div className="text-center py-5" style={{ color: "#64748b" }}>
                                            <div
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    borderRadius: "50%",
                                                    background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: "0 auto 20px",
                                                }}
                                            >
                                                <FiCreditCard style={{ fontSize: "40px", color: "#39b2b4" }} />
                                            </div>
                                            <p style={{ fontSize: "16px", marginBottom: "20px", fontWeight: "500" }}>
                                                No bank accounts added yet.
                                            </p>
                                            <button
                                                className="btn fw-semibold"
                                                onClick={handleAddBank}
                                                style={{
                                                    borderRadius: "10px",
                                                    background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "12px 28px",
                                                    transition: "all 0.3s ease",
                                                    boxShadow: "0 4px 12px rgba(57, 178, 180, 0.3)",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = "translateY(-2px)";
                                                    e.target.style.boxShadow = "0 6px 16px rgba(57, 178, 180, 0.4)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = "translateY(0)";
                                                    e.target.style.boxShadow = "0 4px 12px rgba(57, 178, 180, 0.3)";
                                                }}
                                            >
                                                <FiPlus className="me-2" />
                                                Add Your First Bank Account
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="row g-4">
                                            {banks.map((bank) => (
                                                <div key={bank.id} className="col-12">
                                                    <div
                                                        className="card border-0 shadow-sm"
                                                        style={{
                                                            borderRadius: "12px",
                                                            border: `2px solid ${bank.isPrimary ? "#39b2b4" : "#e9ecef"}`,
                                                            backgroundColor: bank.isPrimary ? "#f0f9ff" : "#ffffff",
                                                            transition: "all 0.3s ease",
                                                            overflow: "hidden",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = "translateY(-4px)";
                                                            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0, 110, 130, 0.15)";
                                                            e.currentTarget.style.borderColor = bank.isPrimary ? "#39b2b4" : "#b3eaf4";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = "translateY(0)";
                                                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                                                            e.currentTarget.style.borderColor = bank.isPrimary ? "#39b2b4" : "#e9ecef";
                                                        }}
                                                    >
                                                        <div className="card-body p-4">
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div className="flex-grow-1">
                                                                    <div className="d-flex align-items-center gap-2 mb-3">
                                                                        <h6 className="mb-0 fw-bold" style={{ color: "#006e82", fontSize: "18px" }}>
                                                                            {bank.bankName}
                                                                        </h6>
                                                                        {bank.isPrimary && (
                                                                            <span
                                                                                className="badge px-3 py-2"
                                                                                style={{
                                                                                    fontSize: "11px",
                                                                                    background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                                                    color: "white",
                                                                                    fontWeight: "600",
                                                                                    borderRadius: "6px",
                                                                                }}
                                                                            >
                                                                                Primary
                                                                            </span>
                                                                        )}
                                                                        {bank.isVerified ? (
                                                                            <div
                                                                                style={{
                                                                                    width: "24px",
                                                                                    height: "24px",
                                                                                    borderRadius: "50%",
                                                                                    background: "#d1fae5",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                }}
                                                                            >
                                                                                <FiCheckCircle style={{ color: "#10b981", fontSize: "16px" }} />
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                style={{
                                                                                    width: "24px",
                                                                                    height: "24px",
                                                                                    borderRadius: "50%",
                                                                                    background: "#fef3c7",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                }}
                                                                            >
                                                                                <FiAlertCircle style={{ color: "#f59e0b", fontSize: "16px" }} />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        className="row g-2"
                                                                        style={{ color: "#64748b", fontSize: "14px" }}
                                                                    >
                                                                        <div className="col-md-6">
                                                                            <span style={{ fontWeight: "600", color: "#006e82" }}>Account:</span>{" "}
                                                                            <span style={{ fontFamily: "monospace" }}>{bank.accountNumber}</span>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <span style={{ fontWeight: "600", color: "#006e82" }}>IFSC:</span>{" "}
                                                                            <span style={{ fontFamily: "monospace" }}>{bank.ifscCode}</span>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <span style={{ fontWeight: "600", color: "#006e82" }}>Branch:</span> {bank.branchName}
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <span style={{ fontWeight: "600", color: "#006e82" }}>Type:</span> {bank.accountType}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    {!bank.isPrimary && (
                                                                        <button
                                                                            className="btn btn-sm"
                                                                            onClick={() => handleSetPrimary(bank.id)}
                                                                            style={{
                                                                                borderRadius: "8px",
                                                                                border: "1px solid #b3eaf4",
                                                                                color: "#006e82",
                                                                                backgroundColor: "#ffffff",
                                                                                transition: "all 0.2s ease",
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.target.style.backgroundColor = "#ccf1f7";
                                                                                e.target.style.borderColor = "#39b2b4";
                                                                                e.target.style.transform = "scale(1.05)";
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.target.style.backgroundColor = "#ffffff";
                                                                                e.target.style.borderColor = "#b3eaf4";
                                                                                e.target.style.transform = "scale(1)";
                                                                            }}
                                                                            title="Set as Primary"
                                                                        >
                                                                            <FiShield />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        onClick={() => handleEditBank(bank)}
                                                                        style={{
                                                                            borderRadius: "8px",
                                                                            border: "1px solid #b3eaf4",
                                                                            color: "#006e82",
                                                                            backgroundColor: "#ffffff",
                                                                            transition: "all 0.2s ease",
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.target.style.backgroundColor = "#ccf1f7";
                                                                            e.target.style.borderColor = "#39b2b4";
                                                                            e.target.style.transform = "scale(1.05)";
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.target.style.backgroundColor = "#ffffff";
                                                                            e.target.style.borderColor = "#b3eaf4";
                                                                            e.target.style.transform = "scale(1)";
                                                                        }}
                                                                        title="Edit"
                                                                    >
                                                                        <FiEdit2 />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        onClick={() => handleDeleteBank(bank.id)}
                                                                        style={{
                                                                            borderRadius: "8px",
                                                                            border: "1px solid #fee2e2",
                                                                            color: "#ef4444",
                                                                            backgroundColor: "#ffffff",
                                                                            transition: "all 0.2s ease",
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.target.style.backgroundColor = "#fee2e2";
                                                                            e.target.style.borderColor = "#ef4444";
                                                                            e.target.style.transform = "scale(1.05)";
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.target.style.backgroundColor = "#ffffff";
                                                                            e.target.style.borderColor = "#fee2e2";
                                                                            e.target.style.transform = "scale(1)";
                                                                        }}
                                                                        title="Delete"
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {!bank.isVerified && (
                                                                <div
                                                                    className="alert py-3 mb-0 border-0 d-flex align-items-center"
                                                                    style={{
                                                                        borderRadius: "8px",
                                                                        backgroundColor: "#fef3c7",
                                                                        border: "1px solid #f59e0b",
                                                                        color: "#92400e",
                                                                        fontSize: "13px",
                                                                    }}
                                                                >
                                                                    <FiAlertCircle className="me-2" style={{ fontSize: "18px" }} />
                                                                    <span>Bank verification pending. OTP will be sent for verification.</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Add/Edit Bank Form */}
                            {showBankForm && (
                                <div className="card shadow-lg border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden", border: "2px solid #b3eaf4" }}>
                                    <div
                                        className="card-header border-0 py-4 px-4 d-flex justify-content-between align-items-center"
                                        style={{
                                         background: "linear-gradient(135deg, rgb(248, 250, 252) 0%, rgb(241, 245, 249) 100%)",
                                        }}
                                    >
                                        <h6 className="mb-0 " style={{ color: "#006e82", fontSize: "18px" }}>
                                            {editingBank ? "Edit Bank Account" : "Add Bank Account"}
                                        </h6>
                                        <button
                                            className="btn btn-sm"
                                            onClick={() => {
                                                setShowBankForm(false);
                                                setEditingBank(null);
                                            }}
                                            style={{
                                                borderRadius: "8px",
                                                border: "none",
                                                color: "#006e82",
                                                backgroundColor: "#c8eeecbf",
                                                transition: "all 0.2s ease",
                                            }}
                                            // onMouseEnter={(e) => {
                                            //     e.target.style.backgroundColor = "rgba(255, 255, 255, 0.8)";
                                            //     e.target.style.transform = "rotate(90deg)";
                                            // }}
                                            // onMouseLeave={(e) => {
                                            //     e.target.style.backgroundColor = "rgba(255, 255, 255, 0.5)";
                                            //     e.target.style.transform = "rotate(0deg)";
                                            // }}
                                        >
                                            <FiX style={{ fontSize: "20px" }} />
                                        </button>
                                    </div>
                                    <div className="card-body p-4" style={{ backgroundColor: "#ffffff" }}>
                                        <form onSubmit={handleSaveBank}>
                                            <div className="row g-4">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                        Account Holder Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="accountHolderName"
                                                        value={bankForm.accountHolderName}
                                                        onChange={handleBankInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "10px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "14px",
                                                            padding: "12px 16px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.15)";
                                                            e.target.style.backgroundColor = "#f8fafc";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                            e.target.style.backgroundColor = "#ffffff";
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                        Account Number <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="accountNumber"
                                                        value={bankForm.accountNumber}
                                                        onChange={handleBankInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "10px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "14px",
                                                            padding: "12px 16px",
                                                            fontFamily: "monospace",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.15)";
                                                            e.target.style.backgroundColor = "#f8fafc";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                            e.target.style.backgroundColor = "#ffffff";
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                        IFSC Code <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="ifscCode"
                                                        value={bankForm.ifscCode}
                                                        onChange={handleBankInputChange}
                                                        required
                                                        maxLength={11}
                                                        style={{
                                                            textTransform: "uppercase",
                                                            borderRadius: "10px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "14px",
                                                            padding: "12px 16px",
                                                            fontFamily: "monospace",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.15)";
                                                            e.target.style.backgroundColor = "#f8fafc";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                            e.target.style.backgroundColor = "#ffffff";
                                                        }}
                                                    />
                                                    <div className="form-text mt-1" style={{ fontSize: "12px", color: "#64748b" }}>
                                                        Format: ABCD0123456
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                        Bank Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="bankName"
                                                        value={bankForm.bankName}
                                                        onChange={handleBankInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "10px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "14px",
                                                            padding: "12px 16px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.15)";
                                                            e.target.style.backgroundColor = "#f8fafc";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                            e.target.style.backgroundColor = "#ffffff";
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                        Branch Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="branchName"
                                                        value={bankForm.branchName}
                                                        onChange={handleBankInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "10px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "14px",
                                                            padding: "12px 16px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.15)";
                                                            e.target.style.backgroundColor = "#f8fafc";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                            e.target.style.backgroundColor = "#ffffff";
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                        Account Type <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className="form-select"
                                                        name="accountType"
                                                        value={bankForm.accountType}
                                                        onChange={handleBankInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "10px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "14px",
                                                            padding: "12px 16px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.15)";
                                                            e.target.style.backgroundColor = "#f8fafc";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                            e.target.style.backgroundColor = "#ffffff";
                                                        }}
                                                    >
                                                        <option value="savings">Savings</option>
                                                        <option value="current">Current</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-3 mt-4 pt-3 border-top" style={{ borderColor: "#b3eaf4" }}>
                                                <button
                                                    type="submit"
                                                    className="btn fw-semibold"
                                                    disabled={isSubmitting}
                                                    style={{
                                                        borderRadius: "10px",
                                                        background: isSubmitting
                                                            ? "#94a3b8"
                                                            : "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                        color: "white",
                                                        border: "none",
                                                        padding: "12px 28px",
                                                        transition: "all 0.3s ease",
                                                        boxShadow: "0 4px 12px rgba(57, 178, 180, 0.3)",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSubmitting) {
                                                            e.target.style.transform = "translateY(-2px)";
                                                            e.target.style.boxShadow = "0 6px 16px rgba(57, 178, 180, 0.4)";
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSubmitting) {
                                                            e.target.style.transform = "translateY(0)";
                                                            e.target.style.boxShadow = "0 4px 12px rgba(57, 178, 180, 0.3)";
                                                        }
                                                    }}
                                                >
                                                    {/* <FiSave className="me-2" /> */}
                                                    {isSubmitting ? "Saving..." : "Save Bank"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn fw-semibold"
                                                    onClick={() => {
                                                        setShowBankForm(false);
                                                        setEditingBank(null);
                                                    }}
                                                    style={{
                                                        borderRadius: "10px",
                                                        border: "2px solid #b3eaf4",
                                                        color: "#006e82",
                                                        backgroundColor: "transparent",
                                                        padding: "12px 28px",
                                                        transition: "all 0.2s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = "#ccf1f7";
                                                        e.target.style.borderColor = "#39b2b4";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = "transparent";
                                                        e.target.style.borderColor = "#b3eaf4";
                                                    }}
                                                >
                                                    {/* <FiX className="me-2" /> */}
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Nominees */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                <div
                                    className="card-header border-0 py-4 px-4 d-flex justify-content-between align-items-center"
                                    style={{
                                      background: "linear-gradient(135deg, rgb(248, 250, 252) 0%, rgb(241, 245, 249) 100%)",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="me-3 d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "48px",
                                                height: "48px",
                                                borderRadius: "12px",
                                                background: "#c8eeecbf",
                                                backdropFilter: "blur(10px)",
                                            }}
                                        >
                                            <FiUser style={{ backgroundColor: "#c8eeecbf", color: "#006e82",     fontSize: "24px" }} />
                                        </div>
                                        <h5 className="mb-0" style={{ color: "#006e82", fontSize: "20px" }}>
                                            Nominees
                                        </h5>
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleAddNominee}
                                     
                                      
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="card-body p-4" style={{ backgroundColor: "#ffffff" }}>
                                    {/* Allocation Summary */}
                                    <div
                                        className="mb-4 p-4 rounded"
                                        style={{
                                            backgroundColor: totalAllocation === 100 ? "#d1fae5" : "#fef3c7",
                                            border: `2px solid ${totalAllocation === 100 ? "#10b981" : "#f59e0b"}`,
                                            borderRadius: "10px",
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span
                                                style={{
                                                    color: totalAllocation === 100 ? "#065f46" : "#92400e",
                                                    fontSize: "14px",
                                                    fontWeight: "600",
                                                }}
                                            >
                                                Total Allocation:
                                            </span>
                                            <span
                                                style={{
                                                    color: totalAllocation === 100 ? "#065f46" : "#92400e",
                                                    fontSize: "20px",
                                                    fontWeight: "700",
                                                }}
                                            >
                                                {totalAllocation}%
                                            </span>
                                        </div>
                                        {totalAllocation !== 100 && (
                                            <div className="mt-2 d-flex align-items-center" style={{ color: "#92400e", fontSize: "12px" }}>
                                                <FiAlertCircle className="me-1" style={{ fontSize: "14px" }} />
                                                Allocation must total 100%
                                            </div>
                                        )}
                                    </div>

                                    {nominees.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: "#64748b" }}>
                                            <div
                                                style={{
                                                    width: "64px",
                                                    height: "64px",
                                                    borderRadius: "50%",
                                                    background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: "0 auto 16px",
                                                }}
                                            >
                                                <FiUser style={{ fontSize: "28px", color: "#39b2b4" }} />
                                            </div>
                                            <p style={{ fontSize: "14px", fontWeight: "500" }}>No nominees added yet.</p>
                                        </div>
                                    ) : (
                                        <div className="mb-3">
                                            {nominees.map((nominee) => (
                                                <div
                                                    key={nominee.id}
                                                    className="mb-3 p-4 rounded"
                                                    style={{
                                                        backgroundColor: "#ccf1f7",
                                                        border: "2px solid #b3eaf4",
                                                        borderRadius: "10px",
                                                        transition: "all 0.2s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#b3eaf4";
                                                        e.currentTarget.style.borderColor = "#39b2b4";
                                                        e.currentTarget.style.transform = "translateX(4px)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#ccf1f7";
                                                        e.currentTarget.style.borderColor = "#b3eaf4";
                                                        e.currentTarget.style.transform = "translateX(0)";
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div className="flex-grow-1">
                                                            <div className="fw-bold mb-1" style={{ color: "#006e82", fontSize: "15px" }}>
                                                                {nominee.name}
                                                            </div>
                                                            <div style={{ color: "#64748b", fontSize: "13px", marginBottom: "8px" }}>
                                                                {nominee.relationship}
                                                            </div>
                                                            <div>
                                                                <span
                                                                    className="badge px-3 py-2"
                                                                    style={{
                                                                        fontSize: "13px",
                                                                        background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                                        color: "white",
                                                                        fontWeight: "600",
                                                                        borderRadius: "6px",
                                                                    }}
                                                                >
                                                                    {nominee.allocation}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex gap-1">
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => handleEditNominee(nominee)}
                                                                style={{
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #b3eaf4",
                                                                    color: "#006e82",
                                                                    backgroundColor: "#ffffff",
                                                                    transition: "all 0.2s ease",
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.backgroundColor = "#ccf1f7";
                                                                    e.target.style.borderColor = "#39b2b4";
                                                                    e.target.style.transform = "scale(1.1)";
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.backgroundColor = "#ffffff";
                                                                    e.target.style.borderColor = "#b3eaf4";
                                                                    e.target.style.transform = "scale(1)";
                                                                }}
                                                                title="Edit"
                                                            >
                                                                <FiEdit2 style={{ fontSize: "14px" }} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => handleDeleteNominee(nominee.id)}
                                                                style={{
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #fee2e2",
                                                                    color: "#ef4444",
                                                                    backgroundColor: "#ffffff",
                                                                    transition: "all 0.2s ease",
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.backgroundColor = "#fee2e2";
                                                                    e.target.style.borderColor = "#ef4444";
                                                                    e.target.style.transform = "scale(1.1)";
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.backgroundColor = "#ffffff";
                                                                    e.target.style.borderColor = "#fee2e2";
                                                                    e.target.style.transform = "scale(1)";
                                                                }}
                                                                title="Delete"
                                                            >
                                                                <FiTrash2 style={{ fontSize: "14px" }} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add/Edit Nominee Form */}
                                    {showNomineeForm && (
                                        <div
                                            className="mt-4 p-4 rounded"
                                            style={{
                                                backgroundColor: "#ffffff",
                                                border: "2px solid #b3eaf4",
                                                borderRadius: "10px",
                                            }}
                                        >
                                            <h6 className="mb-3 fw-bold" style={{ color: "#006e82", fontSize: "16px" }}>
                                                {editingNominee ? "Edit Nominee" : "Add Nominee"}
                                            </h6>
                                            <form onSubmit={handleSaveNominee}>
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "13px" }}>
                                                        Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        name="name"
                                                        value={nomineeForm.name}
                                                        onChange={handleNomineeInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "8px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "13px",
                                                            padding: "10px 14px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                        }}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "13px" }}>
                                                        Relationship <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        name="relationship"
                                                        value={nomineeForm.relationship}
                                                        onChange={handleNomineeInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "8px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "13px",
                                                            padding: "10px 14px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                        }}
                                                    >
                                                        <option value="">Select Relationship</option>
                                                        <option value="Spouse">Spouse</option>
                                                        <option value="Son">Son</option>
                                                        <option value="Daughter">Daughter</option>
                                                        <option value="Father">Father</option>
                                                        <option value="Mother">Mother</option>
                                                        <option value="Brother">Brother</option>
                                                        <option value="Sister">Sister</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "13px" }}>
                                                        Allocation (%) <span className="text-danger">*</span>
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            name="allocation"
                                                            value={nomineeForm.allocation}
                                                            onChange={handleNomineeInputChange}
                                                            required
                                                            min="1"
                                                            max="100"
                                                            style={{
                                                                borderRadius: "8px 0 0 8px",
                                                                borderColor: "#b3eaf4",
                                                                fontSize: "13px",
                                                                padding: "10px 14px",
                                                                transition: "all 0.2s ease",
                                                            }}
                                                            onFocus={(e) => {
                                                                e.target.style.borderColor = "#006e82";
                                                                e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                            }}
                                                            onBlur={(e) => {
                                                                e.target.style.borderColor = "#b3eaf4";
                                                                e.target.style.boxShadow = "none";
                                                            }}
                                                        />
                                                        <span
                                                            className="input-group-text"
                                                            style={{
                                                                backgroundColor: "#ccf1f7",
                                                                borderColor: "#b3eaf4",
                                                                borderRadius: "0 8px 8px 0",
                                                                color: "#006e82",
                                                                borderLeft: "none",
                                                            }}
                                                        >
                                                            <FiPercent style={{ fontSize: "14px" }} />
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "13px" }}>
                                                        Date of Birth <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        name="dateOfBirth"
                                                        value={nomineeForm.dateOfBirth}
                                                        onChange={handleNomineeInputChange}
                                                        required
                                                        style={{
                                                            borderRadius: "8px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "13px",
                                                            padding: "10px 14px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                        }}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "13px" }}>
                                                        PAN Number <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        name="pan"
                                                        value={nomineeForm.pan}
                                                        onChange={handleNomineeInputChange}
                                                        required
                                                        maxLength={10}
                                                        style={{
                                                            textTransform: "uppercase",
                                                            borderRadius: "8px",
                                                            borderColor: "#b3eaf4",
                                                            fontSize: "13px",
                                                            padding: "10px 14px",
                                                            fontFamily: "monospace",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onFocus={(e) => {
                                                            e.target.style.borderColor = "#006e82";
                                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.style.borderColor = "#b3eaf4";
                                                            e.target.style.boxShadow = "none";
                                                        }}
                                                    />
                                                </div>
                                                <div className="d-flex gap-2 mt-3 pt-3 border-top" style={{ borderColor: "#b3eaf4" }}>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-sm fw-semibold flex-grow-1"
                                                        disabled={isSubmitting}
                                                        style={{
                                                            borderRadius: "8px",
                                                            background: isSubmitting
                                                                ? "#94a3b8"
                                                                : "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                            color: "white",
                                                            border: "none",
                                                            padding: "10px 16px",
                                                            transition: "all 0.3s ease",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSubmitting) {
                                                                e.target.style.transform = "translateY(-2px)";
                                                                e.target.style.boxShadow = "0 4px 12px rgba(57, 178, 180, 0.3)";
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSubmitting) {
                                                                e.target.style.transform = "translateY(0)";
                                                                e.target.style.boxShadow = "none";
                                                            }
                                                        }}
                                                    >
                                                        {/* <FiSave className="me-1" /> */}
                                                        Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm fw-semibold"
                                                        onClick={() => {
                                                            setShowNomineeForm(false);
                                                            setEditingNominee(null);
                                                        }}
                                                        style={{
                                                            borderRadius: "8px",
                                                            border: "2px solid #b3eaf4",
                                                            color: "#006e82",
                                                            backgroundColor: "transparent",
                                                            padding: "10px 16px",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = "#ccf1f7";
                                                            e.target.style.borderColor = "#39b2b4";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = "transparent";
                                                            e.target.style.borderColor = "#b3eaf4";
                                                        }}
                                                    >
                                                        <FiX />
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* OTP Verification Modal */}
                    {showOTPModal && (
                        <div
                            className="modal show d-block"
                            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", backdropFilter: "blur(4px)" }}
                            tabIndex="-1"
                        >
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content shadow-lg" style={{ borderRadius: "16px", border: "none", overflow: "hidden" }}>
                                    <div
                                        className="modal-header border-0 py-4 px-4"
                                        style={{
                                           background: "linear-gradient(135deg, rgb(248, 250, 252) 0%, rgb(241, 245, 249) 100%)",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div
                                                style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    borderRadius: "10px",
                                                    background: "#c8eeecbf",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginRight: "12px",
                                                }}
                                            >
                                                <FiShield style={{ color: "#006e82", backgroundColor: "#c8eeecbf", fontSize: "20px" }} />
                                            </div>
                                            <h5 className="modal-title mb-0" style={{ color: "#006e82", fontSize: "20px" }}>
                                                Verify Bank Account
                                            </h5>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-gray"
                                            onClick={() => {
                                                setShowOTPModal(false);
                                                setOtp("");
                                            }}
                                            style={{ fontSize: "14px" }}
                                        />
                                    </div>
                                    <div className="modal-body p-4" style={{ backgroundColor: "#ffffff" }}>
                                        <div
                                            className="alert mb-4 py-3 border-0 d-flex align-items-center"
                                            style={{
                                                borderRadius: "10px",
                                                backgroundColor: "#ccf1f7",
                                                border: "1px solid #b3eaf4",
                                                color: "#006e82",
                                            }}
                                        >
                                            <FiAlertCircle className="me-2" style={{ fontSize: "20px" }} />
                                            <span style={{ fontSize: "14px" }}>
                                                An OTP has been sent to your registered mobile number. Please enter the 6-digit OTP to verify your bank account.
                                            </span>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold mb-3 d-block text-center" style={{ color: "#006e82", fontSize: "15px" }}>
                                                Enter OTP <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control text-center"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                maxLength={6}
                                                placeholder="000000"
                                                style={{
                                                    borderRadius: "12px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "32px",
                                                    letterSpacing: "12px",
                                                    fontWeight: "700",
                                                    padding: "16px",
                                                    color: "#006e82",
                                                    transition: "all 0.2s ease",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.3rem rgba(0, 110, 130, 0.2)";
                                                    e.target.style.backgroundColor = "#f8fafc";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                    e.target.style.backgroundColor = "#ffffff";
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0 p-4" style={{ backgroundColor: "#f8fafc" }}>
                                        <button
                                            type="button"
                                            className="btn fw-semibold"
                                            onClick={() => {
                                                setShowOTPModal(false);
                                                setOtp("");
                                            }}
                                            style={{
                                                borderRadius: "10px",
                                                border: "2px solid #b3eaf4",
                                                color: "#006e82",
                                                backgroundColor: "#ffffff",
                                                padding: "12px 28px",
                                                transition: "all 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = "#ccf1f7";
                                                e.target.style.borderColor = "#39b2b4";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = "#ffffff";
                                                e.target.style.borderColor = "#b3eaf4";
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn fw-semibold"
                                            onClick={handleVerifyOTP}
                                            disabled={otp.length !== 6}
                                            style={{
                                                borderRadius: "10px",
                                                background: otp.length === 6
                                                    ? "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)"
                                                    : "#94a3b8",
                                                color: "white",
                                                border: "none",
                                                padding: "12px 28px",
                                                transition: "all 0.3s ease",
                                                boxShadow: otp.length === 6 ? "0 4px 12px rgba(57, 178, 180, 0.3)" : "none",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (otp.length === 6) {
                                                    e.target.style.transform = "translateY(-2px)";
                                                    e.target.style.boxShadow = "0 6px 16px rgba(57, 178, 180, 0.4)";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (otp.length === 6) {
                                                    e.target.style.transform = "translateY(0)";
                                                    e.target.style.boxShadow = "0 4px 12px rgba(57, 178, 180, 0.3)";
                                                }
                                            }}
                                        >
                                            Verify OTP
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
         
        </>
    );
}
